import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';

const TEX_ARTIST1 = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/kq1h86ag_download.jpg';
const TEX_ARTIST2 = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/1x77s1n5_Honor%20MagicOS%209%20Wallpaper%20-6.jpg';

const ARTISTS = [
  {
    id: 'fromnow',
    name: 'fromnow',
    roleKey: 'Founder / Producer / DJ',
    genre: 'Techno · Deep · Hard-Groove · Dubby',
    bio: 'After losing his entire studio to theft, fromnow rebuilt everything from silence. What remains is not noise // it is intention.',
    texture: TEX_ARTIST1,
    hasPage: true,
  },
  {
    id: 'rvre',
    name: 'RVRE',
    roleKey: 'Artist',
    genre: 'Electronic · Groove',
    bio: 'Something is forming. Stay close.',
    texture: TEX_ARTIST2,
    comingSoon: true,
  },
];

export default function ArtistsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const gridRef = useStaggerReveal('.artist-card', { stagger: 90 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Artists // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;
    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.art-caption', translateY: [20, 0], opacity: [0, 1], duration: 600 })
      .add({ targets: '.art-title', translateY: [50, 0], opacity: [0, 1], duration: 900 }, '-=300');
  }, []);

  // Handle card click - navigate directly to artist page
  const handleArtistClick = (artist) => {
    if (artist.hasPage) {
      navigate(`/artists/${artist.id}`);
    } else if (artist.comingSoon) {
      // Navigate to coming soon page for RVRE
      navigate(`/artists/${artist.id}`);
    }
  };

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>
      <section ref={heroRef} style={{ padding: isMobile ? '60px 20px 40px' : '80px 48px 60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p className="art-caption caption" style={{ marginBottom: '24px', opacity: 0 }}>{t('artists.caption')}</p>
          <h1 className="art-title" style={{
            fontFamily: 'Syne, sans-serif', fontWeight: 800,
            fontSize: isMobile ? '42px' : 'clamp(48px, 8vw, 96px)',
            lineHeight: 0.92, letterSpacing: '-0.03em', textTransform: 'uppercase',
            color: '#F0F4F8', margin: '0 0 20px', opacity: 0,
          }}>
            {t('artists.title')}
          </h1>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '14px' : '15px', color: 'var(--vvs-muted)', maxWidth: '480px', lineHeight: 1.75 }}>
            {t('artists.desc')}
          </p>
        </div>
      </section>

      <section style={{ padding: isMobile ? '20px 20px 80px' : '20px 48px 100px' }}>
        <div ref={gridRef} style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', 
          gap: isMobile ? '16px' : '2px',
        }}>
          {ARTISTS.map(artist => (
            <div 
              key={artist.id} 
              className="artist-card liquid-card vvs-glass" 
              data-testid={`artist-card-${artist.id}`}
              onClick={() => handleArtistClick(artist)}
              style={{ 
                cursor: 'pointer', 
                position: 'relative', 
                overflow: 'hidden', 
                opacity: 0, 
                borderRadius: isMobile ? '8px' : '0',
                transition: 'transform 0.3s ease',
              }}
              onMouseEnter={e => { if (!isMobile) e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { if (!isMobile) e.currentTarget.style.transform = 'translateY(0)'; }}
            >              
              <div style={{ height: isMobile ? '180px' : '240px', position: 'relative', overflow: 'hidden' }}>
                <img 
                  src={artist.texture} 
                  alt={artist.name} 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover', 
                    opacity: 0.5, 
                    transition: 'opacity 0.5s ease, transform 0.6s ease' 
                  }}
                  onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.transform = 'scale(1.04)'; }}}
                  onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.transform = 'scale(1)'; }}}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,22,40,0.9) 0%, transparent 65%)' }} />
                {artist.comingSoon && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '16px', 
                    right: '16px', 
                    padding: '3px 10px', 
                    border: '1px solid rgba(0,240,255,0.25)', 
                    fontFamily: 'Space Grotesk', 
                    fontSize: '10px', 
                    letterSpacing: '0.14em', 
                    color: 'var(--vvs-cyan)', 
                    textTransform: 'uppercase' 
                  }}>
                    {t('artists.incoming')}
                  </div>
                )}
              </div>
              <div style={{ padding: isMobile ? '18px' : '22px' }}>
                <p className="caption" style={{ marginBottom: '5px', color: 'var(--vvs-cyan)' }}>{artist.roleKey}</p>
                <h3 style={{ 
                  fontFamily: 'Syne, sans-serif', 
                  fontWeight: 800, 
                  fontSize: isMobile ? '20px' : '22px', 
                  textTransform: artist.id === 'fromnow' ? 'lowercase' : 'uppercase', 
                  color: '#F0F4F8', 
                  marginBottom: '7px' 
                }}>
                  {artist.name}
                </h3>
                <p style={{ 
                  fontFamily: 'Space Grotesk', 
                  fontSize: '11px', 
                  letterSpacing: '0.1em', 
                  color: 'var(--vvs-muted)', 
                  marginBottom: '14px' 
                }}>
                  {artist.genre}
                </p>
                <span style={{ 
                  fontFamily: 'Space Grotesk', 
                  fontSize: '11px', 
                  letterSpacing: '0.14em', 
                  textTransform: 'uppercase', 
                  color: 'var(--vvs-steel)', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '5px' 
                }}>
                  {artist.comingSoon ? t('artists.coming_soon') : t('artists.open')} <span style={{ fontSize: '13px' }}>→</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
